interface ExclusiveActionProps {
  /** This action is the one in flight — render its spinner. */
  loading: boolean
  /** This or a sibling action is in flight — starting another is blocked. */
  disabled: boolean
}

interface ExclusiveActions<K extends string> {
  /** The action currently in flight, or `null` when the group is idle. */
  busy: K | null
  isBusy: boolean
  /** Button props for one action; spread onto `Button` or a toolbar item. */
  props: (key: K) => ExclusiveActionProps
}

/**
 * Coordinates a group of mutually-exclusive resource actions (save /
 * duplicate / delete / …) so that while any one is in flight, every action
 * in the group is disabled and exactly the in-flight one reports `loading`.
 *
 * Pass each action's existing pending flag (e.g. a TanStack mutation's
 * `isPending`); the result is derived per render, so the group unlocks the
 * moment the mutation settles. Read-only actions that are safe to run
 * concurrently (download, share, version history) should stay out of the
 * group.
 */
export function useExclusiveActions<K extends string>(
  pending: Record<K, boolean>,
): ExclusiveActions<K> {
  const busy = (Object.keys(pending) as K[]).find((key) => pending[key]) ?? null
  return {
    busy,
    isBusy: busy !== null,
    props: (key) => ({ loading: busy === key, disabled: busy !== null }),
  }
}
